/**
 * Replace asset file page.
 * Route: /assets/:id/edit-file
 * For layers G, S, L, M, W only. Uses PUT /assets/{layerPath}/{assetId}/file with multipart assetFile.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, CloudUpload } from '@mui/icons-material';
import assetService from '../api/assetService';
import type { AssetDetailsResponse } from '../contracts/openapi/assetContracts';
import AssetThumbnail from '../components/common/AssetThumbnail';
import type { Asset } from '../types/asset.types';

const LAYER_LABELS: Record<string, string> = {
  G: 'Song',
  S: 'Star',
  L: 'Look',
  M: 'Move',
  W: 'World',
};

const SUPPORTED_LAYERS = new Set(['G', 'S', 'L', 'M', 'W']);

const ALLOWED_FILE_TYPES: Record<string, string[]> = {
  G: ['audio/mpeg', 'audio/wav', 'audio/x-wav'],
  S: ['image/jpeg', 'image/png'],
  L: ['image/jpeg', 'image/png'],
  M: ['video/mp4', 'video/quicktime'],
  W: ['video/mp4', 'video/quicktime'],
};

const ACCEPT_ATTR: Record<string, string> = {
  G: 'audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav',
  S: 'image/jpeg,image/png,.jpg,.jpeg,.png',
  L: 'image/jpeg,image/png,.jpg,.jpeg,.png',
  M: 'video/mp4,video/quicktime,.mp4,.mov',
  W: 'video/mp4,video/quicktime,.mp4,.mov',
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB per API

function detailsToAsset(d: AssetDetailsResponse): Asset {
  return {
    id: d.id,
    _id: d.id,
    name: d.humanFriendlyName ?? '',
    friendlyName: d.humanFriendlyName ?? '',
    machineFriendlyName: d.machineFriendlyAddress ?? '',
    layer: d.layer,
    categoryCode: d.categoryCode ?? d.categoryName ?? '',
    subcategoryCode: d.subcategoryCode ?? d.subcategoryName ?? '',
    type: 'base',
    imageUrl: d.fileUrl ?? '',
    files: [],
    metadata: { fileUrl: d.fileUrl, videoUrl: d.fileUrl },
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    status: 'active',
  };
}

export default function AssetReplaceFilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<AssetDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [replaceError, setReplaceError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) {
      setError('Asset ID is required');
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    assetService
      .getAssetDetails(id)
      .then((res) => {
        if (!cancelled) {
          if (!SUPPORTED_LAYERS.has(res.layer)) {
            setError(`Replacing file is not supported for ${res.layer} assets.`);
            setDetails(null);
          } else {
            setDetails(res);
          }
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message ?? 'Failed to load asset');
          setDetails(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const validateFile = useCallback(
    (file: File): string | null => {
      if (!details) return 'Asset not loaded';
      const allowed = ALLOWED_FILE_TYPES[details.layer] ?? [];
      if (!allowed.includes(file.type)) {
        return `Invalid file type. Allowed: ${allowed.join(', ')}`;
      }
      if (file.size > MAX_FILE_SIZE) {
        return `File size must be at most ${MAX_FILE_SIZE / 1024 / 1024} MB`;
      }
      return null;
    },
    [details]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      setReplaceError(null);
      const err = validateFile(file);
      if (err) {
        setReplaceError(err);
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
    },
    [validateFile]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleReplace = async () => {
    if (!details || !id || !selectedFile) return;
    setUploading(true);
    setReplaceError(null);
    try {
      await assetService.replaceAssetFileByLayer(details.layer, id, selectedFile);
      setSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      setReplaceError(e instanceof Error ? e.message : 'Failed to replace file');
    } finally {
      setUploading(false);
    }
  };

  const handleBack = () => navigate(`/assets/${id}`);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !details) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate('/search-assets')} aria-label="Back" size="small" sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Alert severity="error">{error ?? 'Asset not found'}</Alert>
      </Box>
    );
  }

  const asset = detailsToAsset(details);
  const accept = ACCEPT_ATTR[details.layer] ?? '';

  const taxonomyLabel = [
    details.categoryName ?? details.categoryCode,
    details.subcategoryName ?? details.subcategoryCode,
  ]
    .filter(Boolean)
    .join(' / ');

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header row: back, title, layer chip, taxonomy chip, spacer, Replace file button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 3, pb: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={handleBack} aria-label="Back to asset" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Replace file: {details.humanFriendlyName ?? id}
          </Typography>
          <Chip
            label={LAYER_LABELS[details.layer] ?? details.layer}
            color="primary"
            variant="outlined"
            size="medium"
          />
          {taxonomyLabel && <Chip label={taxonomyLabel} variant="outlined" size="small" />}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="contained"
            disabled={!selectedFile || uploading}
            onClick={handleReplace}
          >
            {uploading ? <CircularProgress size={20} /> : 'Replace file'}
          </Button>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current file
          </Typography>
          <Box
            sx={{
              width: 200,
              height: 200,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.100',
              mb: 3,
            }}
          >
            <AssetThumbnail asset={asset} width={200} height={200} />
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              File replaced successfully. You can go back to the asset or replace again.
            </Alert>
          )}

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            New file (max {MAX_FILE_SIZE / 1024 / 1024} MB)
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: isDragOver ? 'action.hover' : 'background.paper',
              mb: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>

          {replaceError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {replaceError}
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
